/*
 * Copyright (C) 2026 Motorola Solutions, Inc.
 * All Rights Reserved.
 * Motorola Solutions Confidential Restricted.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { PrimeTable, PrimeTableColumn, PrimeTableRef } from '../CustomComponents/PrimeTable';
import { Api } from '../../types/apiData';
import ApiService from '../../services/apiService';
import { useOktaAuth } from '@okta/okta-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '@msi/cobalt-react/button';
import Icon from '@msi/cobalt-react/icon';
import MappingFilter from '../MappingFilter/MappingFilter';
import { IMappingFilterData } from '../../types/mappingFilterData';
import Checkbox from '@msi/cobalt-react/checkbox';
import Overlay from 'react-bootstrap/Overlay';
import Popover from 'react-bootstrap/Popover';
import FormCheck from 'react-bootstrap/FormCheck';
import { useDarkTheme } from '../../contexts/DarkThemeContext';
import GetUpdatesModal from '../GetUpdatesModal/GetUpdatesModal';
import SubscriptionService, { UserSubscription } from '../../services/subscriptionService';
import { addToast } from '@msi/cobalt-react/toast';
import { RowActionsMenu } from './RowActionsMenu';
import { ExpandableDescription } from './ExpandableDescription';

// Constant for field label mapping
const FIELD_LABEL_MAP: Record<string, string> = {
  businessUnit: 'Line of Business',
  product: 'Product',
  area: 'Area',
  apiName: 'API Name',
  version: 'Latest Version',
  dateAdded: 'Date Added',
  productContactEmail: 'Product Point of Contact',
  technicalContactEmail: 'Technical Point of Contact',
  developerProgram: 'Developer Program',
  description: 'Description',
  links: 'Links',
};

interface SubscriptionFeedbackState {
  loading: boolean;
  error: string | null;
  success: boolean;
}

/**
 * TableView Component - API Catalog table with filtering, display columns, and actions
 */
interface TableViewProps {
  onSelectionChange?: (hasSelection: boolean) => void;
  onRegisterExport?: (handler: () => void) => void;
  onRegisterGetUpdates?: (handler: () => void) => void;
}

const TableView: React.FC<TableViewProps> = ({ onSelectionChange, onRegisterExport, onRegisterGetUpdates }) => {
  const { authState } = useOktaAuth();
  const authStateRef = useRef(authState);
  const { isDarkTheme } = useDarkTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const apiService = new ApiService();
  const subscriptionService = new SubscriptionService();
  const tableRef = useRef<PrimeTableRef>(null);
  const settingsTarget = useRef(null);
  
  const [apiData, setApiData] = useState<Api[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [selectedApis, setSelectedApis] = useState<Api[]>([]);
  const selectedApisRef = useRef<Api[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<IMappingFilterData>({
    areaId: "0",
    apiStructure: "0",
    developerProgram: "All",
    managedBy: "All",
    name: null,
  });
  
  // Display columns state - restore from sessionStorage if available
  const [displayColumns, setDisplayColumns] = useState<Record<string, boolean>>(() => {
    const defaults: Record<string, boolean> = {
      businessUnit: true,
      product: true,
      area: true,
      apiName: true,
      version: true,
      dateAdded: false,
      productContactEmail: true,
      technicalContactEmail: true,
      developerProgram: true,
      description: true,
      links: true,
      settings: true, // Settings column is not toggleable by user
    };
    try {
      const stored = sessionStorage.getItem('table-column-visibility');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge stored values into defaults, ignoring unknown keys
        const merged = { ...defaults };
        for (const key of Object.keys(defaults)) {
          if (key in parsed && typeof parsed[key] === 'boolean') {
            merged[key] = parsed[key];
          }
        }
        return merged;
      }
    } catch (e) { /* ignore */ }
    return defaults;
  });
  const [tempDisplayColumns, setTempDisplayColumns] = useState<Record<string, boolean>>(displayColumns);
  const [showDisplayColumnsMenu, setShowDisplayColumnsMenu] = useState(false);
  const [rowMenuCloseSignal, setRowMenuCloseSignal] = useState(0);
  
  // Subscription states
  const [isGetUpdatesModalOpen, setIsGetUpdatesModalOpen] = useState(false);
  const [selectedApiForUpdates, setSelectedApiForUpdates] = useState<Api | null>(null);
  const [subscriptionEmail, setSubscriptionEmail] = useState('');
  const [batchModeCount, setBatchModeCount] = useState<number | null>(null);
  const [subscriptionFeedback, setSubscriptionFeedback] = useState<SubscriptionFeedbackState>({
    loading: false,
    error: null,
    success: false,
  });
  const [subscriptionsByApiId, setSubscriptionsByApiId] = useState<Record<string, string>>({});
  const [updatesActionApiId, setUpdatesActionApiId] = useState<string | null>(null);
  const subscriptionLoadingRef = useRef(false);

  // Parse URL parameters for table state
  const parseTableStateFromURL = () => {
    const searchParams = new URLSearchParams(location.search);
    const sortFieldParam = searchParams.get('sortField') || undefined;
    const sortOrderParam = searchParams.get('sortOrder');
    return {
      page: parseInt(searchParams.get('page') || '0', 10),
      rows: parseInt(searchParams.get('rows') || '20', 10),
      pinnedColumns: searchParams.get('pinned')?.split(',').filter(Boolean).map(s => s.trim()) || [],
      sortField: sortFieldParam,
      sortOrder: sortFieldParam ? (sortOrderParam === '-1' ? -1 : 1) as 1 | -1 : undefined,
    };
  };

  // Initialize table state from URL first, then fall back to sessionStorage
  const initialTableState = (() => {
    const urlState = parseTableStateFromURL();
    // If URL has sort params, use them; otherwise try sessionStorage
    if (urlState.sortField) return urlState;
    try {
      const stored = sessionStorage.getItem('table-sort-state');
      if (stored) {
        const savedSort = JSON.parse(stored);
        return { ...urlState, sortField: savedSort.field, sortOrder: savedSort.order };
      }
    } catch (e) { /* ignore */ }
    return urlState;
  })();
  console.log('[TableView] Initial table state:', initialTableState);
  const [currentPage, setCurrentPage] = useState<number>(initialTableState.page);
  const [rowsPerPage, setRowsPerPage] = useState<number>(initialTableState.rows);
  const [pinnedColumns, setPinnedColumns] = useState<string[]>(initialTableState.pinnedColumns);
  const [sortField, setSortField] = useState<string | undefined>(initialTableState.sortField);
  const [sortOrder, setSortOrder] = useState<1 | -1 | undefined>(initialTableState.sortOrder);

  // Sync table state from URL on back/forward navigation
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlState = parseTableStateFromURL();
    setCurrentPage(urlState.page);
    setRowsPerPage(urlState.rows);
    setPinnedColumns(urlState.pinnedColumns);
    // Only override sort if URL explicitly contains sort params;
    // otherwise preserve current sort (restored from sessionStorage on init)
    if (searchParams.has('sortField')) {
      setSortField(urlState.sortField);
      setSortOrder(urlState.sortOrder);
    }
  }, [location.search]);

  // Update URL with table state
  const updateURLWithTableState = (page: number, rows: number, pinned: string[], sort?: { field?: string; order?: 1 | -1 }) => {
    const searchParams = new URLSearchParams(location.search);
    
    if (page > 0) {
      searchParams.set('page', page.toString());
    } else {
      searchParams.delete('page');
    }
    
    if (rows !== 20) {
      searchParams.set('rows', rows.toString());
    } else {
      searchParams.delete('rows');
    }
    
    if (pinned.length > 0) {
      searchParams.set('pinned', pinned.join(','));
    } else {
      searchParams.delete('pinned');
    }

    const currentSort = sort || { field: sortField, order: sortOrder };
    if (currentSort.field) {
      searchParams.set('sortField', currentSort.field);
      searchParams.set('sortOrder', (currentSort.order || 1).toString());
    } else {
      searchParams.delete('sortField');
      searchParams.delete('sortOrder');
    }

    const newURL = `${location.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    navigate(newURL, { replace: true });
  };

  // Handle filter changes
  const handleFilterChange = async (selectedValues: IMappingFilterData) => {
    setSelectedFilters(selectedValues);
    setLoading(true);
    setCurrentPage(0);

    try {
      const data = (await apiService.getApisByFilter(selectedValues, authState)) as Api[];
      // Filter to show only parent APIs (without parentApiId)
      const parentApis = data.filter(api => !api.parentApiId);
      setApiData(parentApis);
    } catch (error) {
      console.error('Error fetching filtered API data:', error);
      setApiData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterInitialized = async (initialFilters: IMappingFilterData) => {
    console.log('Filters initialized with:', initialFilters);
    setIsInitializing(false);
    // Data loading will be handled by handleFilterChange which gets called by MappingFilter's onChange
  };

  // Helper function to check if at least one column is visible
  const hasAtLeastOneVisibleColumn = (columns: Record<string, boolean>) => {
    return Object.entries(columns).some(([field, isVisible]) =>
      field !== 'settings' && isVisible
    );
  };

  const handleApplyColumns = () => {
    if (hasAtLeastOneVisibleColumn(tempDisplayColumns)) {
      setDisplayColumns(tempDisplayColumns);
      setShowDisplayColumnsMenu(false);
      try {
        sessionStorage.setItem('table-column-visibility', JSON.stringify(tempDisplayColumns));
      } catch (e) { /* ignore */ }
    } else {
      alert('Please select at least one column to display.');
    }
  };

  const getDefaultUserEmail = () => {
    const claims = authState?.idToken?.claims as { email?: string } | undefined;
    return claims?.email ?? '';
  };

  const resolveSubscriptionApiId = (api: Api | null) => api?.parentApiId || api?.id || '';

  const loadUserSubscriptions = useCallback(async () => {
    if (!authState?.isAuthenticated) {
      setSubscriptionsByApiId({});
      return;
    }

    try {
      const userSubscriptions = await subscriptionService.getMySubscriptions(authState);
      const nextSubscriptions = userSubscriptions.reduce<Record<string, string>>((acc, subscription: UserSubscription) => {
        if (subscription.apiId && subscription.id) {
          acc[subscription.apiId] = subscription.id;
        }
        return acc;
      }, {});
      setSubscriptionsByApiId(nextSubscriptions);
    } catch (error) {
      console.error('Error fetching user subscriptions:', error);
    }
  }, [authState]);

  const openGetUpdatesModal = (api: Api | null, countOverride?: number | null) => {
    setSelectedApiForUpdates(api);
    setBatchModeCount(countOverride ?? null);
    setSubscriptionEmail(getDefaultUserEmail());
    setSubscriptionFeedback({ loading: false, error: null, success: false });
    setIsGetUpdatesModalOpen(true);
  };

  const closeGetUpdatesModal = () => {
    setIsGetUpdatesModalOpen(false);
    setSelectedApiForUpdates(null);
    setBatchModeCount(null);
    setSubscriptionEmail('');
    setSubscriptionFeedback({ loading: false, error: null, success: false });
  };

  const clearSubscriptionEmail = () => setSubscriptionEmail('');

  const handleBatchSubscribe = async (normalizedEmail: string) => {
    if (selectedApis.length === 0) {
      addToast('error', 'Please select at least one API to subscribe.', { containerId: 'default' });
      return;
    }

    try {
      setSubscriptionFeedback({ loading: true, error: null, success: false });
      const selectedApiIds = selectedApis.map(api => api.id).filter((id): id is string => !!id);
      const result = await subscriptionService.subscribeBatch(selectedApiIds, normalizedEmail, authState);

      const successes = result.successful.length;
      const failures = result.failed.length;

      if (successes > 0 && failures === 0) {
        addToast('success', `Successfully subscribed to ${successes} API${successes > 1 ? 's' : ''}.`, { containerId: 'default' });
      } else if (successes > 0 && failures > 0) {
        addToast('success', `Subscribed to ${successes} API${successes > 1 ? 's' : ''}.`, { containerId: 'default' });
        const alreadySubscribedCount = result.failed.filter((f: any) => 
          f.error.toLowerCase().includes('already subscribed')
        ).length;
        if (alreadySubscribedCount === failures) {
          addToast('success', `${failures} API${failures > 1 ? 's were' : ' was'} already subscribed.`, { containerId: 'default' });
        }
      } else {
        addToast('error', 'Failed to subscribe to selected APIs.', { containerId: 'default' });
      }

      if (result.successful.length > 0) {
        setSubscriptionsByApiId((prev) => {
          const updated = { ...prev };
          result.successful.forEach(({ apiId, subscriptionId }) => {
            if (apiId && subscriptionId) {
              updated[apiId] = subscriptionId;
            }
          });
          return updated;
        });
      }

      setSubscriptionFeedback({ loading: false, error: null, success: successes > 0 });
      closeGetUpdatesModal();
    } catch (error: any) {
      let errorMessage = 'Unable to subscribe right now. Please try again later.';
      if (error?.response?.data) {
        const data = error.response.data;
        errorMessage = data.message || data.error || errorMessage;
      }
      setSubscriptionFeedback({ loading: false, error: errorMessage, success: false });
      addToast('error', errorMessage, { containerId: 'default' });
    }
  };

  const handleSubscribeToUpdates = async () => {
    const normalizedEmail = subscriptionEmail.trim();

    if (!normalizedEmail) {
      addToast('error', 'Email is required to subscribe.', { containerId: 'default' });
      return;
    }

    if (!authState) {
      addToast('error', 'You must be signed in to subscribe.', { containerId: 'default' });
      return;
    }

    // Batch path
    if (batchModeCount && batchModeCount > 1) {
      await handleBatchSubscribe(normalizedEmail);
      return;
    }

    if (!selectedApiForUpdates?.id) {
      return;
    }

    try {
      setSubscriptionFeedback({ loading: true, error: null, success: false });
      const response = await subscriptionService.subscribeToApi(selectedApiForUpdates.id, normalizedEmail, authState);
      if (response.id) {
        setSubscriptionsByApiId((prev) => ({
          ...prev,
          [selectedApiForUpdates.id]: response.id as string,
        }));
      } else {
        await loadUserSubscriptions();
      }
      setSubscriptionFeedback({ loading: false, error: null, success: false });
      addToast('success', `You are subscribed to updates for ${selectedApiForUpdates.fullApiName || 'this API'}.`, {
        containerId: 'default',
      });
      closeGetUpdatesModal();
    } catch (error: any) {
      let errorMessage = 'Unable to subscribe right now. Please try again later.';
      if (error?.response?.data) {
        const data = error.response.data;
        errorMessage = data.message || data.error || errorMessage;
      }
      setSubscriptionFeedback({ loading: false, error: errorMessage, success: false });
      addToast('error', errorMessage, { containerId: 'default' });
    }
  };

  const handleUnsubscribeFromApi = async (api: Api) => {
    const subscriptionApiId = resolveSubscriptionApiId(api);
    const subscriptionId = subscriptionsByApiId[subscriptionApiId];

    if (!subscriptionApiId || !subscriptionId || !authState) {
      return;
    }

    try {
      setUpdatesActionApiId(subscriptionApiId);
      await subscriptionService.unsubscribe(subscriptionId, authState);
      setSubscriptionsByApiId((prev) => {
        const updated = { ...prev };
        delete updated[subscriptionApiId];
        return updated;
      });
      addToast('success', `You have unsubscribed from updates for ${api.fullApiName || 'this API'}.`, {
        containerId: 'default',
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Unable to unsubscribe right now. Please try again later.';
      addToast('error', errorMessage, {
        containerId: 'default',
      });
    } finally {
      setUpdatesActionApiId(null);
    }
  };

  const handleEditApi = (api: Api, shouldDuplicateItem = false) => {
    const state = shouldDuplicateItem ? { api, shouldDuplicateItem: true } : { api };
    navigate('/add-record', { state });
  };

  const handleDeleteApi = async (id?: string) => {
    if (!id) {
      return;
    }

    setLoading(true);
    try {
      await apiService.deleteApiById(id, authState);
      await handleFilterChange({ ...selectedFilters });
    } catch (error) {
      console.error('Error deleting API:', error);
      alert('Error deleting API. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (event: { first: number; rows: number }) => {
    const newPage = Math.floor(event.first / event.rows);
    setCurrentPage(newPage);
    setRowsPerPage(event.rows);
    updateURLWithTableState(newPage, event.rows, pinnedColumns);
  };

  // Handle sort change
  const handleSortChange = (event: { sortField?: string; sortOrder?: 1 | -1 }) => {
    const newField = event.sortField;
    const newOrder = event.sortOrder;
    setSortField(newField);
    setSortOrder(newOrder);
    updateURLWithTableState(currentPage, rowsPerPage, pinnedColumns, { field: newField, order: newOrder });
    // Persist sort state to sessionStorage as fallback
    try {
      if (newField) {
        sessionStorage.setItem('table-sort-state', JSON.stringify({ field: newField, order: newOrder }));
      } else {
        sessionStorage.removeItem('table-sort-state');
      }
    } catch (e) { /* ignore */ }
  };

  // Handle column pin/unpin
  const handleColumnPinChange = (frozenColumns: string[]) => {
    setPinnedColumns(frozenColumns);
    updateURLWithTableState(currentPage, rowsPerPage, frozenColumns);
  };

  const handleDeleteApiWithEvent = async (id?: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    await handleDeleteApi(id);
  };

  // Export to CSV handler
  const handleExportToCSV = useCallback(async () => {
    const currentSelection = selectedApisRef.current;
    if (currentSelection.length === 0) {
      alert('No API selected for export.');
      return;
    }

    try {
      const selectedApiIds = currentSelection.map(api => api.id).filter((id): id is string => !!id);
      const apiSvc = new ApiService();
      const csvBlob = await apiSvc.exportApisToCSV(selectedApiIds, authStateRef.current);

      // Create and download file
      const link = document.createElement('a');
      const url = URL.createObjectURL(csvBlob);
      link.setAttribute('href', url);
      link.setAttribute('download', `selected_apis_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the object URL
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting APIs to CSV:', error);
      alert('Error exporting APIs to CSV. Please try again.');
    }
  }, []);

  // Keep refs in sync and notify parent of selection changes
  useEffect(() => {
    authStateRef.current = authState;
  }, [authState]);

  useEffect(() => {
    subscriptionLoadingRef.current = subscriptionFeedback.loading;
  }, [subscriptionFeedback.loading]);

  useEffect(() => {
    selectedApisRef.current = selectedApis;
    onSelectionChange?.(selectedApis.length > 0);
  }, [selectedApis, onSelectionChange]);

  useEffect(() => {
    onRegisterExport?.(handleExportToCSV);
  }, [onRegisterExport, handleExportToCSV]);

  useEffect(() => {
    loadUserSubscriptions();
  }, [loadUserSubscriptions]);

  const handleGetUpdatesFromHeader = useCallback(() => {
    if (subscriptionLoadingRef.current) return;
    const currentSelection = selectedApisRef.current;
    if (currentSelection.length === 0) return;
    const selectedApi = currentSelection[0] || null;
    openGetUpdatesModal(selectedApi, currentSelection.length);
  }, []);

  useEffect(() => {
    onRegisterGetUpdates?.(handleGetUpdatesFromHeader);
  }, [onRegisterGetUpdates, handleGetUpdatesFromHeader]);

  // Transform API data for display
  const tableData = apiData.map((api) => ({
    ...api,
    businessUnit: api.linesOfBusiness || 
      (api.areas && api.areas.find((area: any) => area.level === 1)?.name) || '',
    product: api.productNameTechnology || 
      (api.areas && api.areas.find((area: any) => area.level === 2)?.name) || '',
    area: (api.areas && api.areas.find((area: any) => area.level === 3)?.name) || '',
    apiName: api.fullApiName || '',
    version: api.latestApiVersionNumber || '',
    dateAdded: api.dateAdded || '',
    productContactEmail: api.productContactEmail || '',
    technicalContactEmail: api.technicalContactEmail || '',
    developerProgram: api.developerProgram || false,
    description: api.apiDescription || api.description || '',
  }));

  // Generate rowsPerPageOptions with "All" option only if more than 100 records
  const rowsPerPageOptions = tableData.length > 100 
    ? [20, 50, 100, tableData.length] 
    : [20, 50, 100];

  // Calculate dynamic scroll height based on whether actions bar is visible
  const scrollHeight = selectedApis.length > 0 
    ? 'calc(100vh - 430px)' // With actions bar
    : 'calc(100vh - 359px)'; // Without actions bar

  // Handle row click - navigate to details page
  const handleRowClick = (api: Api) => {
    if (api.id) {
      navigate(`/api-details/${api.id}`);
    }
  };

  // Define columns matching ApiTable structure - filter by displayColumns visibility
  const allColumns: PrimeTableColumn<any>[] = [
    {
      field: 'businessUnit',
      header: 'Line of Business',
      sortable: true,
      frozen: false,
      width: '150px',
      minWidth: '135px',
    },
    {
      field: 'product',
      header: 'Product',
      sortable: true,
      frozen: false,
      width: '150px',
      minWidth: '135px',
    },
    {
      field: 'area',
      header: 'Area',
      sortable: true,
      frozen: false,
      width: '150px',
    },
    {
      field: 'apiName',
      header: 'API Name',
      sortable: true,
      width: '200px',
    },
    {
      field: 'version',
      header: 'Latest Version',
      sortable: false,
      align: 'center',
      width: '120px',
    },
    {
      field: 'dateAdded',
      header: 'Date Added',
      sortable: true,
      align: 'center',
      width: '120px',
    },
    {
      field: 'productContactEmail',
      header: 'Product Point of Contact',
      sortable: true,
      width: '200px',
      minWidth: '120px',
      body: (data: any) => (
        <div 
          style={{ 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'normal',
            width: '100%'
          }}
          title={data.productContactEmail || ''}
        >
          {data.productContactEmail || ''}
        </div>
      ),
    },
    {
      field: 'technicalContactEmail',
      header: 'Technical Point of Contact',
      sortable: true,
      width: '200px',
      minWidth: '130px',
      body: (data: any) => (
        <div 
          style={{ 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'normal',
            width: '100%'
          }}
          title={data.technicalContactEmail || ''}
        >
          {data.technicalContactEmail || ''}
        </div>
      ),
    },
    {
      field: 'developerProgram',
      header: 'Developer Program',
      sortable: false,
      align: 'center',
      width: '150px',
      body: (data: any) => (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <input
            type="checkbox"
            checked={data.developerProgram || false}
            disabled
            style={{ pointerEvents: 'none' }}
          />
        </div>
      ),
    },
    {
      field: 'description',
      header: 'Description',
      sortable: false,
      width: '250px',
      body: (data: any) => (
        <ExpandableDescription 
          text={data.description || ''} 
        />
      ),
      minWidth: '120px',
    },
    {
      field: 'links',
      header: 'Links',
      sortable: false,
      width: '150px',
      body: (data: any) => (
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          {data.documentationLink && (
            <span
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                window.open(data.documentationLink, '_blank');
              }}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '4px', 
                cursor: 'pointer',
                color: 'var(--color-primary, #0066cc)'
              }}
            >
              <Icon name="ic_open" size={20} color="primary" />
              <span>Docs</span>
            </span>
          )}
          {data.apiSpecification && (
            <span
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                const url = typeof data.apiSpecification === 'string'
                  ? data.apiSpecification
                  : data.apiSpecification?.url;
                if (url && url.startsWith('https')) {
                  window.open(url, '_blank');
                }
              }}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '4px', 
                cursor: 'pointer',
                color: 'var(--color-primary, #0066cc)'
              }}
            >
              <Icon name="ic_open" size={20} color="primary" />
              <span>Specs</span>
            </span>
          )}
        </div>
      ),
    },
    {
      field: 'settings',
      header: (
        <div ref={settingsTarget} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <a onClick={() => {
            setTempDisplayColumns(displayColumns);
            setShowDisplayColumnsMenu(!showDisplayColumnsMenu);
          }}>
            <Icon name="ic_settings" size={24} color="primary" />
          </a>
          <Overlay
            show={showDisplayColumnsMenu}
            placement="bottom-end"
            target={settingsTarget.current}
            onHide={() => setShowDisplayColumnsMenu(false)}
          >
            <Popover id="popover-contained" className="display-columns-popover">
              <Popover.Header as="h3">
                <Icon name="ic_settings" size={24} color="primary" /> Display Columns
              </Popover.Header>
              <Popover.Body>
                <div className="checkbox-columns">
                  {Object.keys(displayColumns)
                    .filter(field => field !== 'settings')
                    .map((field, index) => (
                      <FormCheck
                        key={index}
                        label={FIELD_LABEL_MAP[field] || field}
                        type="checkbox"
                        checked={tempDisplayColumns[field]}
                        onChange={(e) => {
                          const currentlyChecked = Object.entries(tempDisplayColumns).reduce((count, [f, isVisible]) => {
                            if (f !== 'settings' && isVisible) {
                              return count + 1;
                            }
                            return count;
                          }, 0);

                          if (!e.target.checked && currentlyChecked === 1) {
                            alert('Please select at least one column to display.');
                            return;
                          }

                          setTempDisplayColumns({
                            ...tempDisplayColumns,
                            [field]: e.target.checked,
                          });
                        }}
                      />
                    ))}
                </div>
                <div className="menu-buttons">
                  <Button
                    variant="secondary"
                    onClick={() => setShowDisplayColumnsMenu(false)}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleApplyColumns}>Apply</Button>
                </div>
              </Popover.Body>
            </Popover>
          </Overlay>
        </div>
      ),
      sortable: false,
      width: '80px',
      body: (data: any) => (
        <div style={{ position: 'relative' }}>
          {(() => {
            const subscriptionApiId = resolveSubscriptionApiId(data as Api);
            const isSubscribed = Boolean(subscriptionApiId && subscriptionsByApiId[subscriptionApiId]);
            return (
          <RowActionsMenu
            api={data}
            canEdit={data.userCanEditAndDelete || false}
            onEdit={handleEditApi}
            onGetUpdates={openGetUpdatesModal}
            onUnsubscribe={handleUnsubscribeFromApi}
            isSubscribed={isSubscribed}
            isUpdatesActionDisabled={updatesActionApiId === subscriptionApiId}
            onDelete={handleDeleteApiWithEvent}
            closeSignal={rowMenuCloseSignal}
          />
            );
          })()}
        </div>
      ),
    },
  ];

  // Filter columns based on displayColumns visibility
  const columns = allColumns.filter(col => {
    const field = String(col.field);
    if (field === 'settings') return displayColumns.settings;
    return displayColumns[field] !== false;
  });

  const updatesHelperText = batchModeCount && batchModeCount > 1
    ? `Subscribe to change logs for ${batchModeCount} selected APIs.`
    : selectedApiForUpdates
      ? `Subscribe to the ${selectedApiForUpdates.fullApiName || 'API'} change log to be up to date on recent changes.`
      : 'Subscribe to the API change log to be up to date on recent changes.';

  return (
    <>
      <GetUpdatesModal
        isOpen={isGetUpdatesModalOpen}
        email={subscriptionEmail}
        helperText={updatesHelperText}
        isSubmitting={subscriptionFeedback.loading}
        selectedCount={batchModeCount || undefined}
        onEmailChange={setSubscriptionEmail}
        onClearEmail={clearSubscriptionEmail}
        onSubscribe={handleSubscribeToUpdates}
        onCancel={closeGetUpdatesModal}
      />
      <div style={{ 
        height: 'calc(100vh - 115px - 12vh)', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden',
        marginBottom: '10px'
      }}>
      <div style={{ flexShrink: 0 }}>
        <MappingFilter
          onChange={handleFilterChange}
          onInitialized={handleFilterInitialized}
        />
        {selectedApis.length > 0 && (
          <div style={{
            padding: '10px 20px',
            backgroundColor: isDarkTheme ? '#001526' : '#f8f9fa',
            borderBottom: `1px solid ${isDarkTheme ? 'rgba(255,255,255,0.1)' : '#dee2e6'}`,
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            marginTop: '10px'
          }}>
            <Checkbox
              checked={selectedApis.length === tableData.length && tableData.length > 0}
              indeterminate={selectedApis.length > 0 && selectedApis.length < tableData.length}
              label=""
              onChange={() => {
                if (selectedApis.length === tableData.length) {
                  setSelectedApis([]);
                } else {
                  setSelectedApis(tableData as Api[]);
                }
              }}
            />
            <span style={{ fontSize: '14px', color: isDarkTheme ? 'rgba(255,255,255,0.7)' : '#495057' }}>
              {selectedApis.length} of {tableData.length} selected
            </span>
          </div>
        )}
      </div>
      
      <div style={{ 
        flex: 1, 
        overflow: 'hidden', 
        marginTop: '10px',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        border: `1px solid ${isDarkTheme ? 'rgba(255,255,255,0.12)' : '#e0e0e0'}`,
        borderRadius: '4px',
        backgroundColor: isDarkTheme ? '#0d1f2f' : '#ffffff',
        margin: '17px'
      }}>
        {isInitializing ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <div>Initializing filters...</div>
          </div>
        ) : (
          <PrimeTable
            key={`${sortField}-${sortOrder}`}
            ref={tableRef}
            value={tableData}
            columns={columns}
            dataKey="id"
            paginator
            first={currentPage * rowsPerPage}
            rows={rowsPerPage}
            rowsPerPageOptions={rowsPerPageOptions}
            loading={loading}
            emptyMessage="No APIs found"
            scrollable
            scrollHeight={scrollHeight}
            selectionMode="checkbox"
            selection={selectedApis}
            onSelectionChange={(selection: any) => setSelectedApis(selection as Api[])}
            onRowClick={(e: any) => handleRowClick(e.data)}
            onPage={handlePageChange}
            frozenColumns={pinnedColumns}
            onColumnPin={handleColumnPinChange}
            stripedRows
            showGridlines={false}
            size="normal"
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={handleSortChange}
            exportFilename="api-catalog"
          />
        )}
      </div>
    </div>
    </>
  );
};

export default TableView;
